import { format } from 'd3';
import type { DocumentNode } from 'graphql';
import { get, isNull, isUndefined } from 'lodash';
import type { Client } from 'urql';

import {
  type PayV2EntResp,
  TALENT_DISCOVERY_DATA_V3,
  type TalentDiscoveryDataV3Query,
  type TalentDiscoveryReqV3,
  type TalentDiscoveryRespGeoDataV3,
} from '@revelio/data-access';

import { calculateMarketTightnessMeterValue } from '../../CustomDash/components/DataProvider/market-conditions/z-score/calculate-meter-value';
import {
  buildValNode,
  composeFilterNodes,
  idsToNode,
  wrapExprNode,
} from '../../CustomDash/components/DataProvider/talentDiscovery/node-builders';
import { TalentDiscoveryV3Tags } from '../../CustomDash/components/DataProvider/talentDiscovery/types';
import { GAME_GET_RESULTS_STATS_V2 } from './gameResultStats.gql';

type FetchResultsProps = {
  seniority?: number[];
  role?: number;
  msa?: number;
  prevYearSentimentEndDate?: string;
  postingsEndDate?: string;
  gqlClient: Client;
};

export const fetchResultsStats = async ({
  seniority,
  role,
  msa,
  gqlClient,
}: FetchResultsProps) => {
  // Temporary vars - need to be updated to be simpler
  const vars = {
    entities: [
      {
        EntID: msa,
        EntType: 'MSA',
      },
    ],
    filters: {
      custom_role: {
        entities: [
          {
            id: role,
            levelId: 'role_k150',
          },
        ],
        taxonomyId: {
          default: true,
        },
      },
      end_date: '2025-04',
      raw: true,
      seniority: seniority,
      start_date: '2025-04',
    },
    msa: msa,
    role_k150: role,
    seniority: seniority,
    filtersSentV2: {
      custom_role: {
        entities: [
          {
            id: role,
            levelId: 'role_k150',
          },
        ],
        taxonomyId: {
          default: true,
        },
      },
      end_date: '2025-04',
      msa: [msa],
      seniority: seniority,
      start_date: '2020-01',
    },
  };

  const payReq = {
    entities: [
      {
        EntID: msa,
        EntType: 'MSA',
      },
    ],
    filters: {
      contingent_workers: false,
      custom_role: {
        entities: [
          {
            id: role,
            levelId: 'role_k150',
          },
        ],
        taxonomyId: {
          default: true,
        },
      },
      end_date: '2025-09',
      raw: true,
      seniority: seniority,
      start_date: '2024-01',
    },
  } as const;

  const msaNode =
    typeof msa === 'number'
      ? wrapExprNode(buildValNode(TalentDiscoveryV3Tags.UserMsa, msa))
      : null;

  const roleNode =
    typeof role === 'number'
      ? wrapExprNode(buildValNode(TalentDiscoveryV3Tags.RoleK150, role))
      : null;

  const seniorityExprNode =
    seniority && seniority.length > 0
      ? idsToNode(seniority, TalentDiscoveryV3Tags.Seniority)
      : null;

  const seniorityNode = seniorityExprNode
    ? wrapExprNode(seniorityExprNode)
    : null;

  const tdQuery: TalentDiscoveryReqV3 = {
    filters: {
      expr: composeFilterNodes([msaNode, roleNode, seniorityNode]),
      roles_tax: 'default',
      skills_tax: 0,
    },
    pagination: { page_num: 1, page_size: 5000 },
  };

  const resultsQuery = gqlClient.query(
    GAME_GET_RESULTS_STATS_V2 as DocumentNode,
    { ...vars, payReq }
  );

  const TDQuery = gqlClient.query(TALENT_DISCOVERY_DATA_V3, {
    query: tdQuery,
  });

  const TDRes = await TDQuery.toPromise().catch((e) => {
    console.error('TD Error: ', e);
    return e;
  });

  const res = await resultsQuery.toPromise().catch((e) => {
    console.error('Stats Error:', e);
    return e;
  });

  return { otherStats: res, compensation: res?.data?.pay_v2, td: TDRes };
};

export const calcGrowthYearOverYear = ({
  currentYearMetric,
  previousYearMetric,
}: Record<string, number>) => {
  // @ts-expect-error noUncheckedIndexedAccess
  const yearDifference = currentYearMetric - previousYearMetric;

  // @ts-expect-error noUncheckedIndexedAccess
  const differenceOverYear = yearDifference / previousYearMetric;

  return differenceOverYear;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isMetricPoint = (value: unknown): value is { value: number } =>
  isRecord(value) && typeof value.value === 'number';

export const transformCompensationData = (data: unknown) => {
  const entities = get(data, 'entities', []) as PayV2EntResp[];

  const series = entities.map((entity: PayV2EntResp) => {
    const meta = get(entity, 'metadata', {}) as Record<string, unknown>;
    const histogram = get(entity, 'metrics.histogram', {}) as Record<
      string,
      unknown
    >;
    const totals = get(histogram, 'total', []) as number[];
    const bucketSize = 1000; // total compensation bucket size (matches CustomDash)

    // Derive salary x-values from bucket index
    const salaries: number[] = totals.map((_, idx) => idx * bucketSize);

    // Use raw counts for KDE and compute CDF in [0, 1]
    const totalCount =
      totals.reduce((acc: number, val: number) => acc + (val || 0), 0) || 1;

    let cumulativeCount = 0;
    const kdeValues = totals.map((val) => val || 0);
    const cdf = totals.map((val) => {
      const valid = val || 0;
      cumulativeCount += valid;
      return cumulativeCount / totalCount;
    });

    return {
      id: get(meta, 'id') as string | number | undefined,
      metadata: {
        longName: get(meta, 'long_name') as string | undefined,
        salaries,
        cdf,
      },
      value: kdeValues,
    };
  });

  return { id: 'kde', value: series };
};

export const transformTDResponse = (
  data: TalentDiscoveryDataV3Query | null | undefined,
  id: number
): number => {
  const mapData = data?.talent_discovery_search_v3.map_data ?? [];

  const mapDataPoint = mapData.find(
    (dataPoint): dataPoint is TalentDiscoveryRespGeoDataV3 =>
      dataPoint !== null &&
      dataPoint !== undefined &&
      dataPoint.type === 2 &&
      dataPoint.id === id
  );

  return mapDataPoint?.count ?? 0;
};

export const transformResultsStatsResponse = (data: unknown) => {
  const entities = get(data, 'rawComposition.entities', []) as unknown[];
  const compositionMetrics = get(entities[0], 'metrics', {}) as Record<
    string,
    unknown
  >;

  const posting = get(data, 'posting', []) as unknown[];
  const postingData = get(posting[0], 'category', []) as unknown[];

  const postingMetrics = get(postingData.slice(-1)[0], 'metrics', {}) as Record<
    string,
    unknown
  >;

  const postingMetricsLastFour = postingData;

  let largestPostingMetricLastFour: number | undefined;

  postingMetricsLastFour.forEach((postingMetric: unknown) => {
    const currentMetric = get(postingMetric, 'metrics', {}) as Record<
      string,
      unknown
    >;
    const active = get(currentMetric, 'active', 0) as number;
    if (
      isUndefined(largestPostingMetricLastFour) ||
      active > largestPostingMetricLastFour
    ) {
      largestPostingMetricLastFour = active;
    }
  });

  const sentiment = get(data, 'sentiment', []) as unknown[];

  const sentimentOverall = get(
    sentiment[0],
    'result.scores.overall',
    []
  ) as unknown[];
  const lastSentimentPoint = sentimentOverall[sentimentOverall.length - 1];
  const sentimentMetric = isMetricPoint(lastSentimentPoint)
    ? lastSentimentPoint.value
    : 0;

  const getCompositionMetricValues = (
    metrics: Record<string, unknown>
  ): Record<string, number> => {
    return Object.entries(metrics).reduce<Record<string, number>>(
      (acc, [key, value]) => {
        if (key === '__typename') return acc;

        if (
          Array.isArray(value) &&
          value.length > 0 &&
          isMetricPoint(value[0])
        ) {
          acc[key] = value[0].value;
        }

        return acc;
      },
      {}
    );
  };

  const {
    growth_rate = 0,
    hiring_rate = 0,
    attrition_rate = 0,
    tenure = 0,
    headcount = 0,
  } = getCompositionMetricValues(compositionMetrics);

  // Compute wage growth from salary timeseries (start vs end)
  const salaryData = get(compositionMetrics, 'salary', []) as Array<{
    id?: string | number;
    value: number;
  }>;
  const yearStartWage = salaryData.length > 0 ? salaryData[0]?.value || 0 : 0;
  const yearEndWage =
    salaryData.length > 0 ? salaryData[salaryData.length - 1]?.value || 0 : 0;
  const wageGrowth =
    yearStartWage !== 0 ? (yearEndWage - yearStartWage) / yearStartWage : 0;

  const ttf = get(postingMetrics, 'time_to_fill', 0) as number;
  const activePostings = get(postingMetrics, 'active', 0) as number;

  const TTF_MIN = 5;
  const TTF_MAX = 180;

  const adjusted_time_to_fill = Math.min(TTF_MAX, Math.max(TTF_MIN, ttf));

  // Calculate market tightness using the shared meter logic
  const demandSupply =
    Number(activePostings) && Number(headcount)
      ? Number(activePostings) / Number(headcount)
      : 0;

  const marketTightness = calculateMarketTightnessMeterValue({
    wageGrowth,
    demandSupply,
    timeToFill: adjusted_time_to_fill,
  });

  const transformed = {
    growth: format('.1%')(growth_rate),
    hiring: format('.1%')(hiring_rate),
    attrition: format('.1%')(attrition_rate),
    tenure: `${format('.1f')(tenure)} yrs`,
    sentiment:
      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      !isFinite(sentimentMetric) ||
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      isNaN(sentimentMetric) ||
      isNull(sentimentMetric)
        ? '--'
        : `${sentimentMetric}%`,
    postings: largestPostingMetricLastFour
      ? format(',d')(largestPostingMetricLastFour)
      : '--',
    time_to_fill: adjusted_time_to_fill
      ? `${format('d')(adjusted_time_to_fill)} days`
      : '--',
    market_tightness: String(marketTightness * 100),
  };

  return transformed;
};
